import { baseApi } from "./baseApi";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone?: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: string;
  avatarUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

// 2FA Response when admin login requires OTP verification
export interface TwoFactorResponse {
  requires2FA: true;
  userId: string;
  email: string; // Masked email
}

// Login can return either regular auth or 2FA requirement
export type LoginResponse =
  | { success: boolean; data: { user: User }; message?: string }
  | { success: boolean; data: TwoFactorResponse; message?: string };

export interface Verify2FAData {
  userId: string;
  otp: string;
}

export interface Resend2FAData {
  userId: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  password: string;
}

export interface ValidateResetTokenData {
  token: string;
}

/**
 * Auth API slice
 * Handles authentication-related API calls
 */
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Register new user
    register: builder.mutation<
      { success: boolean; data: AuthResponse; message?: string },
      RegisterData
    >({
      query: (data) => ({
        url: "/auth/register",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    // Login user (may return 2FA requirement for admin users)
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
        credentials: "include", // Include cookies
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    // Verify 2FA OTP code
    verify2FA: builder.mutation<
      { success: boolean; data: { user: User }; message?: string },
      Verify2FAData
    >({
      query: (data) => ({
        url: "/auth/verify-2fa",
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    // Resend 2FA OTP code
    resend2FA: builder.mutation<
      { success: boolean; data: { email: string }; message?: string },
      Resend2FAData
    >({
      query: (data) => ({
        url: "/auth/resend-2fa",
        method: "POST",
        body: data,
      }),
    }),

    // Logout user
    logout: builder.mutation<
      { success: boolean; message?: string },
      void
    >({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
        credentials: "include", // Include cookies
      }),
      invalidatesTags: ["Auth", "User"],
    }),

    // Get user profile
    getProfile: builder.query<
      { success: boolean; data: User; message?: string },
      void
    >({
      query: () => ({
        url: "/auth/profile",
        credentials: "include", // Include cookies
      }),
      providesTags: ["User"],
    }),

    // Request password reset (forgot password)
    forgotPassword: builder.mutation<
      { success: boolean; message?: string },
      ForgotPasswordData
    >({
      query: (data) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body: data,
      }),
    }),

    // Validate reset token
    validateResetToken: builder.mutation<
      { success: boolean; data: { email: string; name: string; role: string }; message?: string },
      ValidateResetTokenData
    >({
      query: (data) => ({
        url: "/auth/validate-reset-token",
        method: "POST",
        body: data,
      }),
    }),

    // Reset password with token
    resetPassword: builder.mutation<
      { success: boolean; message?: string },
      ResetPasswordData
    >({
      query: (data) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useVerify2FAMutation,
  useResend2FAMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useForgotPasswordMutation,
  useValidateResetTokenMutation,
  useResetPasswordMutation,
} = authApi;

